import React, { useEffect, useMemo } from 'react';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';
import { useI18n } from '../contexts/I18nContext';

interface InlineMarkdownEditorProps {
  isVisible: boolean;
  position: { x: number; y: number; width: number; height: number } | null;
  markdown: string;
  fontSize?: number;
  zIndex?: number;
  onMarkdownChange: (markdown: string) => void;
  onComplete: () => void;
  onCancel: () => void;
}

const InlineMarkdownEditor: React.FC<InlineMarkdownEditorProps> = ({
  isVisible,
  position,
  markdown,
  fontSize = 14,
  zIndex = 1001,
  onMarkdownChange,
  onComplete,
  onCancel
}) => {
  const { t } = useI18n();
  // BlockNote 에디터 생성
  const editor = useCreateBlockNote();

  // 마크다운을 BlockNote 블록으로 변환 (처음 열릴 때만)
  useEffect(() => {
    if (editor && isVisible) {
      const loadContent = async () => {
        try {
          if (markdown) {
            const blocks = await editor.tryParseMarkdownToBlocks(markdown);
            editor.replaceBlocks(editor.document, blocks);
          } else {
            // 마크다운이 없으면 빈 문서로 시작
            editor.replaceBlocks(editor.document, [
              {
                type: "paragraph" as const,
                content: "",
              } as any,
            ]);
          }
        } catch (error) {
          console.error('Failed to parse markdown:', error);
          // 에러 발생 시 빈 블록으로
          editor.replaceBlocks(editor.document, [
            {
              type: "paragraph" as const,
              content: "",
            } as any,
          ]);
        }
      };
      loadContent();
    }
  }, [editor, isVisible]);

  // 에디터 내용이 변경될 때 마크다운으로 변환
  const handleChange = async () => {
    if (editor) {
      const markdownText = await editor.blocksToMarkdownLossy(editor.document);
      onMarkdownChange(markdownText);
    }
  };

  // 키보드 단축키 처리
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isVisible) return;

      if (e.key === 'Enter' && e.ctrlKey) {
        // Ctrl+Enter: 입력 완료
        e.preventDefault();
        onComplete();
      } else if (e.key === 'Escape') {
        // Escape: 입력 취소
        e.preventDefault();
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, onComplete, onCancel]);

  if (!isVisible || !position) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: position.width,
        height: position.height,
        zIndex: zIndex,
        border: 'none',
        borderRadius: '8px',
        backgroundColor: 'white',
        overflow: 'hidden',
        boxShadow: 'none',
      }}
    >
      {/* 툴바 */}
      <div style={{ 
        padding: '4px 8px', 
        borderBottom: '1px solid #e5e7eb', 
        display: 'flex', 
        gap: '8px',
        alignItems: 'center',
        background: '#f9fafb'
      }}>
        <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>
          ✨ {t.modals.markdownEditor.notionStyle}
        </span>
        <div style={{ flex: 1 }}></div>
        <span style={{ fontSize: '10px', color: '#9ca3af' }}>
          {t.modals.markdownEditor.hint}
        </span>
      </div>

      {/* BlockNote 에디터 */}
      <div style={{ 
        width: '100%', 
        height: 'calc(100% - 33px)', 
        overflow: 'auto',
        backgroundColor: 'white'
      }}>
        <BlockNoteView
          editor={editor}
          onChange={handleChange}
          theme="light"
        />
      </div>
    </div>
  );
};

export default InlineMarkdownEditor;
